import type { UnifiedFieldDefinition } from '@/lib/fields/types'

import type { CustomFieldDefinition } from '@scrambl/shared'
import {
  ADDRESS_FILTER_PART_LABELS,
  isAddressFilterValue,
  matchesAddressFilter,
} from '@scrambl/shared'

import type { ColumnFilterValue, DataTableColumn } from './types'

export type TableUserOption = { id: string; name: string; email?: string }
export type TableDropdownOption = { value: string; label: string }

export type ColumnFilterMetaOverrides = {
  fieldType?: string
  dropdownOptions?: TableDropdownOption[]
}

type ColumnFilterMetaArgs = {
  columnId: string
  fieldDef?: UnifiedFieldDefinition
  customFields?: CustomFieldDefinition[]
  userOptions?: TableUserOption[]
  overrides?: ColumnFilterMetaOverrides
}

export function getColumnFilterMeta({
  columnId,
  fieldDef,
  customFields = [],
  userOptions,
  overrides,
}: ColumnFilterMetaArgs): Pick<
  DataTableColumn<unknown>,
  'fieldType' | 'dropdownOptions' | 'userOptions' | 'customFieldDefinitionId'
> {
  const normalizeFieldType = (value?: string) => {
    if (!value) return undefined
    switch (value) {
      case 'text':
      case 'email':
      case 'phone':
      case 'link':
      case 'address':
      case 'tags':
      case 'module_search':
      case 'reference':
      case 'multi_reference':
      case 'file':
      case 'attachments':
        return 'STRING'
      case 'textarea':
      case 'richtext':
        return 'TEXT'
      case 'number':
      case 'percentage':
        return 'NUMBER'
      case 'date':
        return 'DATE'
      case 'datetime':
        return 'DATETIME'
      case 'time':
        return 'DATE'
      case 'select':
        return 'DROPDOWN'
      case 'multiselect':
        return 'MULTISELECT'
      case 'boolean':
        return 'BOOLEAN'
      case 'user':
        return 'USER'
      case 'multi_user':
        return 'MULTI_USER'
      default:
        return value.toUpperCase()
    }
  }

  const customFieldForColumn = fieldDef?.isCustomField
    ? customFields.find(cf => cf.fieldName === fieldDef.name)
    : columnId.startsWith('custom_')
      ? customFields.find(
          cf => cf.fieldName === columnId.replace('custom_', ''),
        )
      : undefined
  // ADDRESS custom fields filter by one address part (city, country, ...).
  // Standard address columns keep the plain text filter: their list APIs
  // only understand text.
  const isAddressCustomField =
    !overrides?.fieldType &&
    (fieldDef?.sourceFieldType === 'ADDRESS' ||
      customFieldForColumn?.fieldType === 'ADDRESS')
  const fieldType = isAddressCustomField
    ? 'ADDRESS'
    : normalizeFieldType(overrides?.fieldType ?? fieldDef?.fieldType)
  const dropdownOptions =
    overrides?.dropdownOptions ??
    fieldDef?.options?.map(opt => ({ value: opt.value, label: opt.label }))

  const needsUserOptions = fieldType === 'USER' || fieldType === 'MULTI_USER'

  let customFieldDefinitionId: string | undefined
  if (fieldDef?.isCustomField) {
    const customField = customFields.find(cf => cf.fieldName === fieldDef.name)
    customFieldDefinitionId = customField?.id
  } else if (columnId.startsWith('custom_')) {
    const fieldName = columnId.replace('custom_', '')
    const customField = customFields.find(cf => cf.fieldName === fieldName)
    customFieldDefinitionId = customField?.id
  }

  return {
    fieldType,
    dropdownOptions,
    userOptions: needsUserOptions ? userOptions : undefined,
    customFieldDefinitionId,
  }
}

type ColumnValueGetter<T> = (item: T, columnId: string) => unknown

const hasFilterValue = (filterValue?: ColumnFilterValue | null) => {
  if (!filterValue) return false
  if (filterValue.type === 'address') {
    return (
      isAddressFilterValue(filterValue.value) &&
      filterValue.value.text.trim() !== ''
    )
  }
  if (filterValue.value === null || filterValue.value === undefined)
    return false
  if (Array.isArray(filterValue.value) && filterValue.value.length === 0)
    return false
  if (typeof filterValue.value === 'string' && filterValue.value.trim() === '')
    return false
  return true
}

const ADDRESS_KEYS = [
  'street',
  'city',
  'state',
  'postalCode',
  'zipCode',
  'country',
] as const

export const getFilterableText = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value)
  }
  if (value instanceof Date) return value.toISOString()
  if (Array.isArray(value)) {
    return value
      .map(item => getFilterableText(item))
      .filter(Boolean)
      .join(' ')
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>
    const formatted =
      typeof obj.formatted === 'string' ? obj.formatted.trim() : ''
    if (formatted) return formatted

    const named = obj.name ?? obj.label ?? obj.title
    if (
      typeof named === 'string' ||
      typeof named === 'number' ||
      typeof named === 'boolean'
    ) {
      const namedText = String(named).trim()
      if (namedText) return namedText
    }

    const addressParts = ADDRESS_KEYS.map(key => {
      const part = obj[key]
      if (typeof part === 'string' || typeof part === 'number') {
        return String(part).trim()
      }
      return ''
    }).filter(Boolean)
    if (addressParts.length > 0) {
      return addressParts.join(', ')
    }

    const primitiveValues = Object.values(obj)
      .map(item => {
        if (
          typeof item === 'string' ||
          typeof item === 'number' ||
          typeof item === 'boolean'
        ) {
          return String(item).trim()
        }
        if (item instanceof Date) return item.toISOString()
        return ''
      })
      .filter(Boolean)
    if (primitiveValues.length > 0) {
      return primitiveValues.join(' ')
    }
  }
  return ''
}

const matchesUserFilter = (value: unknown, selectedUsers: string[]) => {
  const matchesUser = (item: unknown): boolean => {
    if (typeof item === 'string') {
      return selectedUsers.includes(item)
    }
    if (item && typeof item === 'object') {
      type UserLike = {
        id?: string
        userId?: string | null
        memberId?: string | null
      }

      if ('user' in item && (item as { user?: UserLike }).user) {
        const user = (item as { user: UserLike }).user
        if (
          [user.id, user.userId, user.memberId].some(
            id => typeof id === 'string' && selectedUsers.includes(id),
          )
        ) {
          return true
        }
      }
      if (
        'userId' in item &&
        typeof (item as { userId?: string }).userId === 'string'
      ) {
        return selectedUsers.includes((item as { userId: string }).userId)
      }
      if (
        'memberId' in item &&
        typeof (item as { memberId?: string }).memberId === 'string'
      ) {
        return selectedUsers.includes((item as { memberId: string }).memberId)
      }
      if ('id' in item && typeof (item as { id?: string }).id === 'string') {
        return selectedUsers.includes((item as { id: string }).id)
      }
    }
    return false
  }

  if (Array.isArray(value)) {
    return value.some(matchesUser)
  }

  return matchesUser(value)
}

const matchesSelectFilter = (value: unknown, selectedValues: string[]) => {
  const matchesValue = (item: unknown): boolean => {
    if (typeof item === 'string' || typeof item === 'number') {
      return selectedValues.includes(String(item))
    }
    if (item && typeof item === 'object') {
      if ('id' in item && typeof (item as { id?: string }).id === 'string') {
        return selectedValues.includes((item as { id: string }).id)
      }
      if (
        'value' in item &&
        typeof (item as { value?: string }).value === 'string'
      ) {
        return selectedValues.includes((item as { value: string }).value)
      }
    }
    return false
  }

  if (Array.isArray(value)) {
    return value.some(matchesValue)
  }

  return matchesValue(value)
}

const matchesTextFilter = (value: unknown, searchText: string) => {
  const normalized = getFilterableText(value).toLowerCase()
  if (!normalized) return false
  return normalized.includes(searchText)
}

const matchesDateFilter = (
  value: unknown,
  range: { min?: string; max?: string },
) => {
  if (!range.min && !range.max) return true
  if (!value) return false
  const dateValue = value instanceof Date ? value : new Date(value as string)
  if (Number.isNaN(dateValue.getTime())) return false

  const dateOnly = new Date(
    dateValue.getFullYear(),
    dateValue.getMonth(),
    dateValue.getDate(),
  )

  if (range.min) {
    const minDate = new Date(range.min)
    if (!Number.isNaN(minDate.getTime())) {
      const minDateOnly = new Date(
        minDate.getFullYear(),
        minDate.getMonth(),
        minDate.getDate(),
      )
      if (dateOnly < minDateOnly) return false
    }
  }

  if (range.max) {
    const maxDate = new Date(range.max)
    if (!Number.isNaN(maxDate.getTime())) {
      const maxDateOnly = new Date(
        maxDate.getFullYear(),
        maxDate.getMonth(),
        maxDate.getDate(),
      )
      if (dateOnly > maxDateOnly) return false
    }
  }

  return true
}

export function applyColumnFilters<T>(
  data: T[],
  columnFilters: Record<string, ColumnFilterValue>,
  getValue: ColumnValueGetter<T>,
): T[] {
  if (!columnFilters || Object.keys(columnFilters).length === 0) return data

  const activeFilters = Object.entries(columnFilters).filter(([, value]) =>
    hasFilterValue(value),
  )
  if (activeFilters.length === 0) return data

  return data.filter(item => {
    for (const [columnId, filterValue] of activeFilters) {
      if (!filterValue) continue
      const itemValue = getValue(item, columnId)

      switch (filterValue.type) {
        case 'text': {
          const searchText = String(filterValue.value).toLowerCase()
          if (!matchesTextFilter(itemValue, searchText)) return false
          break
        }
        case 'number': {
          const range = filterValue.value as { min?: number; max?: number }
          const parsed =
            typeof itemValue === 'number'
              ? itemValue
              : parseFloat(String(itemValue || ''))
          if (Number.isNaN(parsed)) return false
          if (range.min !== undefined && parsed < range.min) return false
          if (range.max !== undefined && parsed > range.max) return false
          break
        }
        case 'boolean': {
          const boolValue = filterValue.value as boolean
          let itemBool = false
          if (typeof itemValue === 'boolean') {
            itemBool = itemValue
          } else if (typeof itemValue === 'string') {
            const lower = itemValue.toLowerCase()
            itemBool = lower === 'true' || lower === 'yes' || lower === '1'
          } else if (typeof itemValue === 'number') {
            itemBool = itemValue !== 0
          }
          if (itemBool !== boolValue) return false
          break
        }
        case 'date': {
          const range = filterValue.value as { min?: string; max?: string }
          if (!matchesDateFilter(itemValue, range)) return false
          break
        }
        case 'select': {
          const selectedValues = filterValue.value as string[]
          if (!selectedValues || selectedValues.length === 0) break
          if (!matchesSelectFilter(itemValue, selectedValues)) return false
          break
        }
        case 'user': {
          const selectedUsers = filterValue.value as string[]
          if (!selectedUsers || selectedUsers.length === 0) break
          if (!matchesUserFilter(itemValue, selectedUsers)) return false
          break
        }
        case 'address': {
          if (!isAddressFilterValue(filterValue.value)) break
          if (!matchesAddressFilter(itemValue, filterValue.value)) return false
          break
        }
        default:
          break
      }
    }

    return true
  })
}

/**
 * Filter-bar chip text for an ADDRESS sub-field filter, e.g.
 * `Office — City contains "spring"`. Null when the value isn't one.
 */
export function getAddressFilterChipLabel(
  columnName: string,
  filterValue: ColumnFilterValue,
): string | null {
  if (
    filterValue.type !== 'address' ||
    !isAddressFilterValue(filterValue.value)
  ) {
    return null
  }
  const { part, operator, text } = filterValue.value
  if (!text.trim()) return null
  const partLabel =
    part === 'any' ? '' : ` — ${ADDRESS_FILTER_PART_LABELS[part]}`
  const verb = operator === 'equals' ? 'is' : 'contains'
  return `${columnName}${partLabel} ${verb} "${text.trim()}"`
}

/** An ADDRESS filter whose text is blank — applying it clears the filter. */
export function isBlankAddressFilter(
  filterValue: ColumnFilterValue | null | undefined,
): boolean {
  return (
    filterValue?.type === 'address' &&
    isAddressFilterValue(filterValue.value) &&
    filterValue.value.text.trim() === ''
  )
}
